/**
 * Carts Vuex module for Portal Application
 * Controls the data and actions for the various Carts
 * 
 * @module Carts
 * @see
 * @author Bailey Kulman (Fresche Solutions)
 * @date June 2023
 */

import portalapi from "@services/portalapi";
import { duplicateHandlingFunctions } from "@services/duplicatesService";
import { resetStateData } from "@/utils/vuexStoreUtils";
import { getField, updateField } from "vuex-map-fields";

const DEFAULT_INITIAL_CART = {
    orderTotal: 0,
    futureTotal: 0,
    headerText: null,
    purchaseOrderNumber: null,
    items: [],
};

const DEFAULT_INITIAL_CLAIM_CART = {
    orderTotal: 0,
    headerText: null,
    numberOfBoxes: null,
    items: [],
};

const DEFAULT_STATE = {
    regularCart: DEFAULT_INITIAL_CART,
    storeCart: DEFAULT_INITIAL_CART,
    claimCart: DEFAULT_INITIAL_CLAIM_CART,
    quickOrderList: [],
    claimReturnCodes: [],
    regularCartLoadingCount: 0,
    storeCartLoadingCount: 0,
    claimCartLoadingCount: 0,
};

const state = structuredClone(DEFAULT_STATE);

const getters = {
    getField, // for vuex-map-fields

    getRegularCart(st) {
        return st.regularCart;
    },

    getStoreCart(st) {
        return st.storeCart;
    },

    getClaimCart(st) {
        return st.claimCart;
    },

    getQuickOrderList(st) {
        return st.quickOrderList;
    },

    getRegularCartLoadingCount(st) {
        return st.cartLoadingCount ?? 0;
    },

    getClaimReturnCodes(st) {
        return st.claimReturnCodes;
    },    

    getStoreCartLoadingCount(st) {
        return st.cartLoadingCount ?? 0;
    },

    getClaimCartLoadingCount(st) {
        return st.claimCartLoadingCount ?? 0;
    },
};

const mutations = {
    updateField, // for vuex-map-fields

    async clearStateData(st) {
        st = resetStateData(st, DEFAULT_STATE);
    },
    
    setRegularCart(st, payload) {
        st.regularCart = payload;
    },
    
    setStoreCart(st, payload) {
        st.storeCart = payload;
    },

    setClaimCart(st, payload) {
        st.claimCart = payload;
    },

    setQuickOrderList(st, payload) {
        st.quickOrderList = payload;
    },

    addToQuickOrderList(st, payload) {
        // Get line number and sortingGroup for item
        let lineNum = st.quickOrderList.length > 0 ? Math.max(...st.quickOrderList.map(item => item.lineNum)) + 1 : 1;
        let matchingItems = st.quickOrderList.filter(item => item.itemNumber === payload.itemNumber).map(item => item.lineNum);
        let sortingGroup = matchingItems.length > 0 ? Math.min(...matchingItems) : lineNum;

        payload.lineNum = lineNum;
        payload.sortingGroup = sortingGroup;

        duplicateHandlingFunctions.addItemToList(st.quickOrderList, payload, 'itemNumber', duplicateHandlingFunctions.compareListItems);
    },

    updateQuickOrderItemQuantity(st, payload) {
        st.quickOrderList[payload.index] = payload.item;
    },

    removeFromQuickOrderList(st, index) {
        duplicateHandlingFunctions.deleteItemFromList(st.quickOrderList, index, 'itemNumber');
    },

    consolidateQuickOrderItem(st, payload) {
        duplicateHandlingFunctions.consolidateItem(st.quickOrderList, payload, 'itemNumber');
    },

    setClaimReturnCodes(st, payload) {
        st.claimReturnCodes = payload;
    },

    incrementCartLoadingCount(st, cartType) {
        if (cartType === vm.config.globalProperties.$constants.CART_TYPE_REGULAR) {
            st.regularCartLoadingCount++;
        } else if (cartType === vm.config.globalProperties.$constants.CART_TYPE_STORE) {
            st.storeCartLoadingCount++;
        } else if (cartType === vm.config.globalProperties.$constants.CART_TYPE_CLAIM) {
            st.claimCartLoadingCount++;
        }
    },

    decrementCartLoadingCount(st, cartType) {
        if (cartType === vm.config.globalProperties.$constants.CART_TYPE_REGULAR) {
            st.regularCartLoadingCount--;
        } else if (cartType === vm.config.globalProperties.$constants.CART_TYPE_STORE) {
            st.storeCartLoadingCount--;
        } else if (cartType === vm.config.globalProperties.$constants.CART_TYPE_CLAIM) {
            st.claimCartLoadingCount--;
        }
    },
};

const actions = {
    // Get cart items
    async fetchCart({ commit }, cartType) 
    {
        commit("incrementCartLoadingCount", cartType);
        const response = await portalapi.moduleCarts.getCart(cartType);
        commit("decrementCartLoadingCount", cartType);
    
        if (response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
            response.data.items.sort(duplicateHandlingFunctions.compareListItems);

            if (cartType === vm.config.globalProperties.$constants.CART_TYPE_REGULAR) {
                commit("setRegularCart", response.data);
            } else if (cartType === vm.config.globalProperties.$constants.CART_TYPE_STORE) {
                commit("setStoreCart", response.data);
            } else if (cartType === vm.config.globalProperties.$constants.CART_TYPE_CLAIM) {
                commit("setClaimCart", response.data);
            }
        } 
    },


    // Add items to cart
    async addItemsToCart({ commit, dispatch }, payload) 
    {
        commit("incrementCartLoadingCount", payload.cartType);
        let response = await portalapi.moduleCarts.addItemsToCart(payload);
        commit("decrementCartLoadingCount", payload.cartType);

        await actions.updateCartData({ commit, dispatch }, { cartType: payload.cartType, response: response });
    
        return response;
    },


    // Remove item from the cart
    async removeItemFromCart({ commit, dispatch }, payload) 
    {
        commit("incrementCartLoadingCount", payload.cartType);
        const response = await portalapi.moduleCarts.removeItemFromCart(payload.cartType, payload.cartLineId);
        commit("decrementCartLoadingCount", payload.cartType);

        await actions.updateCartData({ commit, dispatch }, { cartType: payload.cartType, response: response });
    },


    // Remove all items from cart
    async emptyCart({ commit, dispatch }, cartType) 
    {
        commit("incrementCartLoadingCount", cartType);
        const response = await portalapi.moduleCarts.emptyCart(cartType);
        commit("decrementCartLoadingCount", cartType);

        await actions.updateCartData({ commit, dispatch }, { cartType: cartType, response: response });
    },


    // Update quantity of cart line item
    async updateCartItemQuantity({ commit, dispatch }, payload) 
    {
        commit("incrementCartLoadingCount", payload.cartType);
        const response = await portalapi.moduleCarts.updateCartItemQuantity(payload);
        commit("decrementCartLoadingCount", payload.cartType);

        await actions.updateCartData({ commit, dispatch }, { cartType: payload.cartType, response: response });
    },


    // Update added text for cart line item
    async updateCartItemAddedText({ commit, dispatch }, payload)
    {
        commit("incrementCartLoadingCount", payload.cartType);
        let result = await portalapi.moduleCarts.updateCartItemAddedText(payload);
        commit("decrementCartLoadingCount", payload.cartType);

        await actions.updateCartData({ commit, dispatch }, { cartType: payload.cartType, response: result });

        return result;
    },  


    // Update reason code for claim cart line item
    async updateClaimCartItemReasonCode({ commit, dispatch }, payload)
    {
        commit("incrementCartLoadingCount", vm.config.globalProperties.$constants.CART_TYPE_CLAIM);
        let result = await portalapi.moduleCarts.updateClaimCartItemReasonCode(payload);
        commit("decrementCartLoadingCount", vm.config.globalProperties.$constants.CART_TYPE_CLAIM);

        await actions.updateCartData({ commit, dispatch }, { cartType: vm.config.globalProperties.$constants.CART_TYPE_CLAIM, response: result });

        return result;
    },


    // Update invoice number for claim cart line item
    async updateClaimCartItemInvoiceNumber({ commit, dispatch }, payload)
    {
        commit("incrementCartLoadingCount", vm.config.globalProperties.$constants.CART_TYPE_CLAIM);
        let result = await portalapi.moduleCarts.updateClaimCartItemInvoiceNumber(payload);
        commit("decrementCartLoadingCount", vm.config.globalProperties.$constants.CART_TYPE_CLAIM);

        // Check if the result is an error object (from the global interceptor)
        if (result && result.response && result.response.status >= 400) {
            throw result; // Re-throw as an error so component can catch it
        }

        await actions.updateCartData({ commit, dispatch }, { cartType: vm.config.globalProperties.$constants.CART_TYPE_CLAIM, response: result });

        return result;
    },


     // Update claim cart number of boxes
    async updateClaimCartNumberOfBoxes({ commit, dispatch }, payload) 
    {
        commit("incrementCartLoadingCount", vm.config.globalProperties.$constants.CART_TYPE_CLAIM);
        let result = await portalapi.moduleCarts.updateClaimCartNumberOfBoxes(payload);
        commit("decrementCartLoadingCount", vm.config.globalProperties.$constants.CART_TYPE_CLAIM);

        await actions.updateCartData({ commit, dispatch }, { cartType:  vm.config.globalProperties.$constants.CART_TYPE_CLAIM, response: result });

        return result;
    },   
    

    // Update expiry date for claim cart line item
    async updateClaimCartItemExpiryDate({ commit, dispatch }, payload)
    {
        commit("incrementCartLoadingCount", vm.config.globalProperties.$constants.CART_TYPE_CLAIM);
        let result = await portalapi.moduleCarts.updateClaimCartItemExpiryDate(payload);
        commit("decrementCartLoadingCount", vm.config.globalProperties.$constants.CART_TYPE_CLAIM);

        await actions.updateCartData({ commit, dispatch }, { cartType: vm.config.globalProperties.$constants.CART_TYPE_CLAIM, response: result });

        return result;
    },


    // Consolidate duplicated cart items
    async consolidateDuplicatedItem({ commit, dispatch }, payload) 
    {
        commit("incrementCartLoadingCount", payload.cartType);
        const response = await portalapi.moduleCarts.consolidateCartItems(payload);
        commit("decrementCartLoadingCount", payload.cartType);

        await actions.updateCartData({ commit, dispatch }, { cartType: payload.cartType, response: response });
    },


    // Update cart purchase order number
    async updateCartPurchaseOrderNumber({ commit, dispatch }, payload) 
    {
        commit("incrementCartLoadingCount", payload.cartType);
        let result = await portalapi.moduleCarts.updateCartPurchaseOrderNumber(payload);
        commit("decrementCartLoadingCount", payload.cartType);

        await actions.updateCartData({ commit, dispatch }, { cartType: payload.cartType, response: result });

        return result;
    },


    // Update cart header text
    async updateCartHeaderText({ commit, dispatch }, payload) 
    {
        commit("incrementCartLoadingCount", payload.cartType);
        let result = await portalapi.moduleCarts.updateCartHeaderText(payload);
        commit("decrementCartLoadingCount", payload.cartType);

        await actions.updateCartData({ commit, dispatch }, { cartType: payload.cartType, response: result });

        return result;
    },


    // Submit cart
    async submitCart({ commit }, payload) 
    {
        commit("incrementCartLoadingCount", payload.cartType);
        let response = await portalapi.moduleCarts.submitCart(payload.cartType, payload.adjustmentData);
        commit("decrementCartLoadingCount", payload.cartType);

        if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
            if (payload.cartType === vm.config.globalProperties.$constants.CART_TYPE_CLAIM) {
                actions.fetchCart({ commit }, vm.config.globalProperties.$constants.CART_TYPE_CLAIM);
            } else {
                actions.fetchCart({ commit }, vm.config.globalProperties.$constants.CART_TYPE_STORE);
                actions.fetchCart({ commit }, vm.config.globalProperties.$constants.CART_TYPE_REGULAR);
            }
        }
        
        return response?.data || response?.response?.data;
    },


    // Add item to quick orders list
    async addToQuickOrderList({ commit }, payload) 
    {
        const response = await portalapi.moduleItem.getItemListingData(payload);

        if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
            commit("addToQuickOrderList", response.data);

            return true;
        }

        return false;
    },


    // Update quick order item quantity
    async updateQuickOrderItemQuantity({ commit }, payload) 
    {
        const response = await portalapi.moduleItem.getItemListingData(payload);

        if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
            let data = {
                index: payload.index,
                item: response.data
            };

            commit("updateQuickOrderItemQuantity", data);

            return true;
        }

        return false;
    },


    // Remove item from quick orders list
    async removeFromQuickOrderList({ commit }, index) 
    {
        commit("removeFromQuickOrderList", index);
    },


    // Consolidate duplicate quick order item
    async consolidateQuickOrderItem({ commit }, payload) 
    {
        let response = await portalapi.moduleItem.getItemListingData(payload);

        if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
            commit("consolidateQuickOrderItem", response.data);
        }
    },


    // Empty the quick order list
    async emptyQuickOrderList({ commit }) 
    {
        await commit("setQuickOrderList", []);
    },


    // Add quick order items to regular cart
    async addQuickOrderItemsToCart(context) 
    {
        let commit = context.commit;
        let items = context.state.quickOrderList;

        const payload = {
            items: items.map((item) => {
                return {
                    'itemNumber': item.itemNumber,
                    'quantity': item.quantity,
                };
            }),
            cartType: vm.config.globalProperties.$constants.CART_TYPE_REGULAR,
        };

        await actions.addItemsToCart({ commit }, payload);

        actions.emptyQuickOrderList({ commit });
    },


    // Upload orders to cart
    async uploadOrdersToCart({ commit, dispatch }, payload)
    {
        let cartType = payload.cartType;
        let payloadData = payload.payloadData;
        let labelType = payload.labelType;

        const response = await portalapi.moduleUploadHandHeldFile.uploadOrdersToCart(cartType, payloadData, labelType);

        if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
            actions.reloadCart({ commit, dispatch }, cartType);

            return response.data;
        }
        
        return false;
    },


    // Reload Cart data based on cart type
    async reloadCart({ commit, dispatch }, cartType)
    {
        if (cartType === vm.config.globalProperties.$constants.CART_TYPE_REGULAR || cartType === vm.config.globalProperties.$constants.CART_TYPE_STORE || cartType === vm.config.globalProperties.$constants.CART_TYPE_CLAIM) {
            actions.fetchCart({ commit }, cartType);
        } else if (cartType === vm.config.globalProperties.$constants.CART_TYPE_LABEL) {
            await dispatch("LabelItems/fetchLabelItemsCart", null, { root: true });
        }
    },


    // Get cart validation
    async getCartValidation({ commit }, cartType) 
    {
        const response = await portalapi.moduleCarts.getCartValidation(cartType);

        if (response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
            return response.data;
        } else {
            throw new Error("Failed to fetch cart validation");
        }
    },


    // Add regular cart items to store cart
    async addRegularCartItemsToStoreCart({ commit, dispatch }) 
    {
        commit("incrementCartLoadingCount", vm.config.globalProperties.$constants.CART_TYPE_REGULAR);
        const response = await portalapi.moduleCarts.addRegularCartItemsToStoreCart();
        commit("decrementCartLoadingCount", vm.config.globalProperties.$constants.CART_TYPE_REGULAR);

        await actions.updateCartData({ commit, dispatch }, { 
            cartType: vm.config.globalProperties.$constants.CART_TYPE_STORE, 
            response: response,
        });

        if (response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
            actions.reloadCart({ commit, dispatch }, vm.config.globalProperties.$constants.CART_TYPE_REGULAR);

            return response.data;
        }
    },


    // Update cart data based on cart type
    async updateCartData({ commit, dispatch }, payload)
    {
        if (
            payload.response 
            && payload.response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK 
            && payload.response.data.status === vm.config.globalProperties.$constants.STATUS_SUCCESS
            && payload.response.data.updatedCart
        ) {
            let updatedCart = payload.response.data.updatedCart;
            updatedCart.items.sort(duplicateHandlingFunctions.compareListItems);

            if (payload.cartType === vm.config.globalProperties.$constants.CART_TYPE_REGULAR) {
                commit("setRegularCart", updatedCart);
            } else if (payload.cartType === vm.config.globalProperties.$constants.CART_TYPE_STORE) {
                commit("setStoreCart", updatedCart);
            } else if (payload.cartType === vm.config.globalProperties.$constants.CART_TYPE_CLAIM) {
                commit("setClaimCart", updatedCart);
            } else if (payload.cartType === vm.config.globalProperties.$constants.CART_TYPE_LABEL) {
                await dispatch("LabelItems/updateLabelItemsCartData", updatedCart, { root: true });
            }
        }

        return;
    },

    
    // Get claim return codes
    async fetchClaimReturnCodes(context) 
    {
        let commit = context.commit;

        if (context.state.claimReturnCodes.length === 0) {
            const response = await portalapi.moduleOrders.getClaimReturnCodes();

            if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
                commit("setClaimReturnCodes", response.data);
            } 
        }
    },

    incrementRegularCartLoadingCount({ commit }) 
    {
        commit("incrementCartLoadingCount", vm.config.globalProperties.$constants.CART_TYPE_REGULAR);
    },


    decrementRegularCartLoadingCount({ commit }) 
    {
        commit("decrementCartLoadingCount", vm.config.globalProperties.$constants.CART_TYPE_REGULAR);
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
